import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Upload as UploadIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Left-pane metadata form for the Blog Editor. Handles title / subtitle / dates /
 * author byline+record / excerpt / header image / SEO / modules / slug / blog_id.
 * Also owns the "Replace with new .docx" button. Every field mutates the shared
 * `blog` state via `updateField`.
 */
export default function BlogEditorMetadata({ blog, authors, updateField, reuploading, replaceDocx, setBlog }) {
  return (
    <>
      <div className="grid md:grid-cols-6 gap-3">
        <div className="md:col-span-6">
          <label className="text-xs uppercase tracking-widest text-slate-500">Title</label>
          <Input data-testid="edit-blog-title" value={blog.title} onChange={e => updateField("title", e.target.value)}/>
        </div>
        <div className="md:col-span-6">
          <label className="text-xs uppercase tracking-widest text-slate-500">Subtitle</label>
          <Input data-testid="edit-blog-subtitle" value={blog.subtitle || ""}
                 placeholder="Optional — displayed under the title"
                 onChange={e => updateField("subtitle", e.target.value)}/>
        </div>
        <div className="md:col-span-3">
          <label className="text-xs uppercase tracking-widest text-slate-500">
            Release date <span className="text-slate-400 normal-case">(blank = draft)</span>
          </label>
          <Input data-testid="edit-blog-release-date" type="date"
                 value={blog.release_date || ""} onChange={e => updateField("release_date", e.target.value || null)}/>
          {blog.release_date && blog.release_date > new Date().toISOString().slice(0,10) && (
            <p className="text-xs text-amber-700 mt-1">Scheduled — hidden from public until this date.</p>
          )}
          {!blog.release_date && <p className="text-xs text-slate-500 mt-1">Draft — hidden from public.</p>}
        </div>
        <div className="md:col-span-6">
          <label className="text-xs uppercase tracking-widest text-slate-500">Author</label>
          <div className="flex gap-2 items-stretch">
            <Input data-testid="edit-blog-author-name" value={blog.author_name || ""}
                   placeholder="Byline — e.g. Brian H. Black, D.O."
                   className="flex-1"
                   onChange={e => updateField("author_name", e.target.value)}/>
            <Select value={blog.author_id || "none"} onValueChange={v => updateField("author_id", v === "none" ? null : v)}>
              <SelectTrigger data-testid="edit-blog-author" className="w-48 flex-none" title="Optionally link to an Author record for the bio card + /authors/:slug page">
                <SelectValue placeholder="Link record…"/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— No linked record —</SelectItem>
                {authors.map(a => <SelectItem key={a.author_id} value={a.author_id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            The byline text shows on the blog. Linking to an author record adds the bio card + author page.
          </p>
        </div>
        <div className="md:col-span-3">
          <label className="text-xs uppercase tracking-widest text-slate-500">First published</label>
          <Input data-testid="edit-blog-first-published" type="date"
                 value={blog.first_published || ""}
                 onChange={e => updateField("first_published", e.target.value || null)}/>
        </div>
        <div className="md:col-span-3">
          <label className="text-xs uppercase tracking-widest text-slate-500">Last updated</label>
          <Input data-testid="edit-blog-last-updated" type="date"
                 value={blog.last_updated || ""}
                 onChange={e => updateField("last_updated", e.target.value || null)}/>
        </div>
        <div className="md:col-span-6">
          <label className="text-xs uppercase tracking-widest text-slate-500">Excerpt</label>
          <Textarea rows={2} value={blog.excerpt || ""} onChange={e => updateField("excerpt", e.target.value)}/>
        </div>
      </div>

      {/* Header image + SEO / module metadata (parsed from docx trailer) */}
      <details className="border border-slate-200 rounded-md p-3 bg-slate-50 open:pb-4" open>
        <summary className="text-xs uppercase tracking-widest text-slate-500 cursor-pointer select-none">
          Header image & Metadata
        </summary>
        <div className="grid md:grid-cols-6 gap-3 mt-3">
          <div className="md:col-span-6">
            <label className="text-xs uppercase tracking-widest text-slate-500">Header image URL</label>
            <Input data-testid="edit-blog-header-image-url" value={blog.header_image_url || ""}
                   placeholder="/api/files/…  — auto-set when uploading a .docx with a header image"
                   onChange={e => updateField("header_image_url", e.target.value)}/>
            {blog.header_image_url && (
              <img src={blog.header_image_url} alt="" className="mt-2 max-h-40 rounded border border-slate-200"/>
            )}
          </div>
          <div className="md:col-span-3">
            <label className="text-xs uppercase tracking-widest text-slate-500">Header alt text</label>
            <Input data-testid="edit-blog-header-image-alt" value={blog.header_image_alt || ""}
                   onChange={e => updateField("header_image_alt", e.target.value)}/>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs uppercase tracking-widest text-slate-500">Header filename</label>
            <Input data-testid="edit-blog-header-image-filename" value={blog.header_image_filename || ""}
                   onChange={e => updateField("header_image_filename", e.target.value)}/>
          </div>
          <div className="md:col-span-6">
            <label className="text-xs uppercase tracking-widest text-slate-500">SEO meta description</label>
            <Textarea rows={2} data-testid="edit-blog-seo-description"
                      value={blog.seo_description || ""}
                      onChange={e => updateField("seo_description", e.target.value)}/>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs uppercase tracking-widest text-slate-500">Primary module</label>
            <Input data-testid="edit-blog-primary-module" value={blog.primary_module || ""}
                   placeholder="e.g. 1C.4"
                   onChange={e => updateField("primary_module", e.target.value)}/>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs uppercase tracking-widest text-slate-500">
              Secondary modules <span className="text-slate-400 normal-case">(comma separated)</span>
            </label>
            <Input data-testid="edit-blog-secondary-modules"
                   value={(blog.secondary_modules || []).join(", ")}
                   placeholder="e.g. 4A.2, 5B.1"
                   onChange={e => updateField("secondary_modules",
                     e.target.value.split(/[,;/]/).map(s => s.trim()).filter(Boolean))}/>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs uppercase tracking-widest text-slate-500">Slug</label>
            <Input data-testid="edit-blog-slug" value={blog.slug || ""}
                   onChange={e => updateField("slug", e.target.value)}/>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs uppercase tracking-widest text-slate-500">Blog ID</label>
            <BlogIdRenameField blog={blog} onRenamed={(next) => setBlog(next)}/>
          </div>
        </div>
      </details>

      <div className="border border-slate-200 rounded-md p-3 bg-slate-50 flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500">Replace with new .docx</div>
        <label className="text-xs text-sky-700 cursor-pointer hover:text-sky-900 inline-flex items-center gap-1">
          <UploadIcon className="w-3.5 h-3.5"/>{reuploading ? "Parsing…" : "Choose file"}
          <input type="file" accept=".docx" className="hidden" onChange={e => replaceDocx(e.target.files[0])}/>
        </label>
      </div>
    </>
  );
}

/**
 * BlogIdRenameField — inline editor for the persisted blog_id. Read-only by
 * default (Blog IDs are permanent URLs); a Rename button reveals input + Save/Cancel.
 * Save hits POST /api/blogs/{id}/rename-id which sanitizes to safe URL chars and
 * refuses 409 collisions with live blogs.
 */
function BlogIdRenameField({ blog, onRenamed }) {
  const [editing, setEditing] = useState(false);
  const [next, setNext] = useState(blog?.blog_id || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNext(blog?.blog_id || ""); }, [blog?.blog_id]);
  const trimmed = next.trim();
  const isDirty = trimmed && trimmed !== (blog?.blog_id || "");

  const save = async () => {
    if (!isDirty) return;
    setBusy(true);
    try {
      const r = await api.post(`/blogs/${encodeURIComponent(blog.blog_id)}/rename-id`, { blog_id: trimmed });
      toast.success(`Blog ID → ${r.data.blog_id}`);
      onRenamed?.(r.data);
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <div className="flex items-stretch gap-2">
        <Input value={blog?.blog_id || ""} readOnly disabled
               className="bg-slate-100 font-mono text-xs flex-1"
               data-testid="edit-blog-id-readonly"/>
        <Button type="button" variant="outline" size="sm"
                onClick={() => setEditing(true)}
                data-testid="edit-blog-id-rename-btn"
                title="Rename this blog's persistent ID">
          Rename
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <div className="flex items-stretch gap-2">
        <Input value={next} autoFocus
               data-testid="edit-blog-id-input"
               onChange={e => setNext(e.target.value)}
               onKeyDown={e => {
                 if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); save(); }
                 // Radix Dialog listens on Escape at document level to close;
                 // stop propagation so cancel only exits rename mode.
                 if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setEditing(false); setNext(blog.blog_id); }
               }}
               className="font-mono text-xs flex-1"/>
        <Button type="button" size="sm"
                disabled={!isDirty || busy}
                onClick={save}
                data-testid="edit-blog-id-save-btn">
          {busy ? "Saving…" : "Save"}
        </Button>
        <Button type="button" variant="outline" size="sm"
                disabled={busy}
                onClick={() => { setEditing(false); setNext(blog.blog_id); }}
                data-testid="edit-blog-id-cancel-btn">
          Cancel
        </Button>
      </div>
      <p className="text-[11px] text-amber-700 leading-tight">
        Changing the Blog ID will break any external links or bookmarks pointing at the current ID.
      </p>
    </div>
  );
}
